'use client'

import { useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'
import Button from '@/components/ui/Button'
import { useToast } from '@/components/ui/ToastContainer'
import MembershipRequestModal from '@/components/MembershipRequestModal'

interface MembershipStatus {
  status: 'NOT_MEMBER' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'ADMIN'
  role?: 'ADMIN' | 'SUPER_ADMIN'
  requestedAt?: string
  approvedAt?: string
  rejectedAt?: string
  rejectionReason?: string
}

interface ClubMembershipButtonProps {
  clubId: string
  clubName: string
  registrationUrl?: string | null
}

export default function ClubMembershipButton({ clubId, clubName, registrationUrl }: ClubMembershipButtonProps) {
  const { data: session, status } = useSession()
  const { showToast } = useToast()
  const [membershipStatus, setMembershipStatus] = useState<MembershipStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [showRequestModal, setShowRequestModal] = useState(false)

  useEffect(() => {
    if (status === 'authenticated') {
      fetchMembershipStatus()
    } else if (status === 'unauthenticated') {
      setLoading(false)
    }
  }, [status, clubId])

  async function fetchMembershipStatus() {
    try {
      const response = await fetch(`/api/clubs/${clubId}/membership`)
      if (response.ok) {
        const data = await response.json()
        setMembershipStatus(data)
      } else if (response.status === 401) {
        // Not authenticated - this is fine
        setMembershipStatus({ status: 'NOT_MEMBER' })
      }
    } catch (error) {
      console.error('Error fetching membership status:', error)
    } finally {
      setLoading(false)
    }
  }

  function handleRequestClick() {
    setShowRequestModal(true)
  }

  async function confirmMembershipRequest() {
    setShowRequestModal(false)
    setActionLoading(true)
    try {
      const response = await fetch(`/api/clubs/${clubId}/membership`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (response.ok) {
        showToast({
          type: 'success',
          title: 'Membership Requested',
          message: `Your request to join ${clubName} has been sent to club admins for approval.`
        })
        await fetchMembershipStatus()
      } else {
        const data = await response.json()
        showToast({
          type: 'error',
          title: 'Request Failed',
          message: data.error || 'Failed to request membership'
        })
      }
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Request Failed',
        message: 'An error occurred while requesting membership'
      })
    } finally {
      setActionLoading(false)
    }
  }

  async function cancelRequest() {
    if (window.confirm('Are you sure you want to cancel your membership request?')) {
      setActionLoading(true)
      try {
        const response = await fetch(`/api/clubs/${clubId}/membership`, {
          method: 'DELETE'
        })

        if (response.ok) {
          showToast({
            type: 'success',
            title: 'Request Cancelled',
            message: 'Your membership request has been cancelled.'
          })
          await fetchMembershipStatus()
        } else {
          const data = await response.json()
          showToast({
            type: 'error',
            title: 'Cancellation Failed',
            message: data.error || 'Failed to cancel request'
          })
        }
      } catch (error) {
        showToast({
          type: 'error',
          title: 'Cancellation Failed',
          message: 'An error occurred while cancelling request'
        })
      } finally {
        setActionLoading(false)
      }
    }
  }

  async function leaveClub() {
    if (window.confirm(`Are you sure you want to leave ${clubName}?`)) {
      setActionLoading(true)
      try {
        const response = await fetch(`/api/clubs/${clubId}/membership`, {
          method: 'DELETE'
        })

        if (response.ok) {
          showToast({
            type: 'success',
            title: 'Left Club',
            message: `You have left ${clubName}.`
          })
          await fetchMembershipStatus()
        } else {
          const data = await response.json()
          showToast({
            type: 'error',
            title: 'Failed to Leave',
            message: data.error || 'Failed to leave club'
          })
        }
      } catch (error) {
        showToast({
          type: 'error',
          title: 'Failed to Leave',
          message: 'An error occurred while leaving club'
        })
      } finally {
        setActionLoading(false)
      }
    }
  }

  // Don't show anything if not logged in
  if (status !== 'authenticated') {
    return null
  }

  // Don't show anything while loading
  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-9 bg-gray-200 rounded"></div>
      </div>
    )
  }

  // Don't show button for admins
  if (membershipStatus?.status === 'ADMIN') {
    return null
  }

  // Show different button based on membership status
  if (membershipStatus?.status === 'APPROVED') {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className="font-medium">Member</span>
        </div>
        <Button
          onClick={leaveClub}
          size="sm"
          variant="secondary"
          className="w-full"
          disabled={actionLoading}
        >
          {actionLoading ? 'Leaving...' : 'Leave Club'}
        </Button>
      </div>
    )
  }

  if (membershipStatus?.status === 'PENDING') {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-yellow-700 bg-yellow-50 px-3 py-2 rounded-lg">
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="font-medium">Pending Approval</span>
        </div>
        <Button
          onClick={cancelRequest}
          size="sm"
          variant="secondary"
          className="w-full"
          disabled={actionLoading}
        >
          {actionLoading ? 'Cancelling...' : 'Cancel Request'}
        </Button>
      </div>
    )
  }

  if (membershipStatus?.status === 'REJECTED') {
    return (
      <div className="space-y-2">
        <div className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-lg">
          <div className="flex items-center gap-2 font-medium mb-1">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span>Request Declined</span>
          </div>
          {membershipStatus.rejectionReason && (
            <p className="text-xs ml-7">{membershipStatus.rejectionReason}</p>
          )}
        </div>
        <Button
          onClick={handleRequestClick}
          size="sm"
          className="w-full"
          disabled={actionLoading}
        >
          {actionLoading ? 'Requesting...' : 'Request Again'}
        </Button>
      </div>
    )
  }

  // Default: Not a member
  return (
    <>
      <Button
        onClick={handleRequestClick}
        size="sm"
        className="w-full"
        disabled={actionLoading}
      >
        {actionLoading ? 'Requesting...' : 'Request to Join'}
      </Button>
      
      <MembershipRequestModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        onConfirm={confirmMembershipRequest}
        clubName={clubName}
        registrationUrl={registrationUrl}
        loading={actionLoading}
      />
    </>
  )
}

